import React from 'react';
import PropTypes from 'prop-types';
import { Layer, Rect, Line, Group } from 'react-konva';

/**
 * FogOfWarMask - Visual fog overlay for BattleRoom stage
 *
 * Renders a black mask over the map area with reveal and cover operations applied in order.
 * Uses globalCompositeOperation to carve out holes (destination-out) for revealed regions.
 *
 * date_of_change: 2025-09-24
 * Implementation notes:
 * - The base mask covers the entire current map area using map position and size
 * - Each operation is stored in map-local normalized coordinates (0..1) and resolved to pixels at render time
 * - 'reveal' ops use destination-out to cut holes; 'cover' ops draw black on top to re-add fog
 * - The mask layer is non-interactive; input handling is implemented by a separate interaction layer
 */
const FogOfWarMask = ({
  enabled = false,
  mapPosition = { x: 0, y: 0 },
  mapSize = { width: 0, height: 0 },
  operations = [],
  opacity = 0.85,
}) => {
  // @date_of_change: 2025-10-04 - Removed spammy render logging

  if (!enabled || !mapSize?.width || !mapSize?.height) return null;

  const { x, y } = mapPosition;
  const { width, height } = mapSize;

  /**
   * GAP-003: Convert normalized point array to flat Konva points array [x1,y1,x2,y2,...]
   * Rule reference: GAP-003 spec — geometry stored normalized, resolved to pixels at render time.
   * date_of_change: 2026-05-20
   */
  const toAbsPoints = (pointsPct) => {
    if (!Array.isArray(pointsPct)) return [];
    const flat = [];
    for (const pt of pointsPct) {
      flat.push(x + (pt.xPct || 0) * width);
      flat.push(y + (pt.yPct || 0) * height);
    }
    return flat;
  };

  // Helper to convert normalized rect to absolute stage rect
  const toAbsRect = (rectPct) => {
    const rx = x + (rectPct.xPct || 0) * width;
    const ry = y + (rectPct.yPct || 0) * height;
    const rw = (rectPct.wPct || 0) * width;
    const rh = (rectPct.hPct || 0) * height;

    // Clamp to map bounds
    const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
    const absX = clamp(rx, x, x + width);
    const absY = clamp(ry, y, y + height);
    const absW = clamp(rw, 0, width);
    const absH = clamp(rh, 0, height);

    return { x: absX, y: absY, width: absW, height: absH };
  };

  return (
    <Layer className="fog-of-war-layer" listening={false}>
      {/* Base black mask covering the entire map area */}
      <Rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill="black"
        opacity={opacity}
        listening={false}
      />

      {/* Apply operations in order: cover draws black; reveal carves holes */}
      <Group listening={false}>
        {operations.map((op, idx) => {
          // GAP-003: polygon/lasso ops use pointsPct; rect ops use rectPct
          // date_of_change: 2026-05-20
          if (op.shape === 'polygon' || op.shape === 'lasso') {
            const pts = toAbsPoints(op.pointsPct);
            if (pts.length < 6) return null; // need at least 3 points (6 values)

            if (op.type === 'reveal') {
              return (
                <Line
                  key={`fog-poly-reveal-${idx}`}
                  points={pts}
                  closed={true}
                  fill="white"
                  stroke={null}
                  strokeWidth={0}
                  listening={false}
                  globalCompositeOperation="destination-out"
                  perfectDrawEnabled={false}
                />
              );
            }
            return (
              <Line
                key={`fog-poly-cover-${idx}`}
                points={pts}
                closed={true}
                fill="black"
                opacity={opacity}
                stroke={null}
                strokeWidth={0}
                listening={false}
                globalCompositeOperation="source-over"
                perfectDrawEnabled={false}
              />
            );
          }

          // Rectangle operations (legacy + new)
          const abs = toAbsRect(op.rectPct || op.rect || {});
          if (!abs.width || !abs.height) return null;

          if (op.type === 'reveal') {
            return (
              <Rect
                key={`fog-reveal-${idx}`}
                x={abs.x}
                y={abs.y}
                width={abs.width}
                height={abs.height}
                fill="white"
                listening={false}
                globalCompositeOperation="destination-out"
              />
            );
          }

          // Default to 'cover' if unknown
          // @date_of_change: 2025-10-04 - Use full opacity for cover operations to prevent layering
          // Cover operations restore fog to match the base mask, not create semi-transparent layers
          return (
            <Rect
              key={`fog-cover-${idx}`}
              x={abs.x}
              y={abs.y}
              width={abs.width}
              height={abs.height}
              fill="black"
              opacity={opacity}
              listening={false}
              globalCompositeOperation="source-over"
            />
          );
        })}
      </Group>
    </Layer>
  );
};

FogOfWarMask.propTypes = {
  enabled: PropTypes.bool,
  mapPosition: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
  mapSize: PropTypes.shape({ width: PropTypes.number, height: PropTypes.number }),
  operations: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['reveal', 'cover']).isRequired,
      // Rectangle operation (legacy + default)
      rectPct: PropTypes.shape({
        xPct: PropTypes.number,
        yPct: PropTypes.number,
        wPct: PropTypes.number,
        hPct: PropTypes.number,
      }),
      // GAP-003: Polygon / lasso operation — date_of_change: 2026-05-20
      shape: PropTypes.oneOf(['polygon', 'lasso']),
      pointsPct: PropTypes.arrayOf(
        PropTypes.shape({ xPct: PropTypes.number, yPct: PropTypes.number })
      ),
    })
  ),
  opacity: PropTypes.number,
};

export default FogOfWarMask;
